import {
  jobWorkOrderSchema,
  outsidePremisesSchema,
  vendorPerformanceSchema,
  vendorSchema,
  vendorSettlementSchema,
  vendorStatementSchema,
  type CreateJobWorkInput,
  type CreateVendorInput,
  type JobWorkOrder,
  type OutsidePremises,
  type ReceiveJobWorkInput,
  type RecordVendorAdvanceInput,
  type RecordVendorDeductionInput,
  type SettleVendorInput,
  type Vendor,
  type VendorPerformance,
  type VendorSettlement,
  type VendorStatement,
} from '@tailoring-crm/shared';
import { z } from 'zod';
import type { ApiClient } from '../api/client.js';

/**
 * Every job-work call the app makes.
 *
 * The settlement call is the only one that moves money, and it takes the job-work orders being
 * paid for rather than an amount — the server computes what is owed from the accepted pieces,
 * the deductions and the advances. A client that sent a figure would be a client that could send
 * the wrong one.
 */

const BASE = '/outsourcing';

export async function getVendors(client: ApiClient, includeInactive = false): Promise<Vendor[]> {
  const body = await client.get(`${BASE}/vendors`, {
    schema: z.object({ items: z.array(vendorSchema) }),
    ...(includeInactive ? { query: { all: 'true' } } : {}),
  });
  return body.items;
}

export async function createVendor(client: ApiClient, input: CreateVendorInput): Promise<Vendor> {
  return await client.post(`${BASE}/vendors`, input, { schema: vendorSchema });
}

export async function getVendorStatement(
  client: ApiClient,
  vendorId: string,
): Promise<VendorStatement> {
  return await client.get(`${BASE}/vendors/${vendorId}/statement`, {
    schema: vendorStatementSchema,
  });
}

export async function recordAdvance(
  client: ApiClient,
  vendorId: string,
  input: RecordVendorAdvanceInput,
): Promise<unknown> {
  return await client.post(`${BASE}/vendors/${vendorId}/advances`, input);
}

export async function recordDeduction(
  client: ApiClient,
  vendorId: string,
  input: RecordVendorDeductionInput,
): Promise<unknown> {
  return await client.post(`${BASE}/vendors/${vendorId}/deductions`, input);
}

export async function settleVendor(
  client: ApiClient,
  vendorId: string,
  input: SettleVendorInput,
): Promise<VendorSettlement> {
  return await client.post(`${BASE}/vendors/${vendorId}/settle`, input, {
    schema: vendorSettlementSchema,
  });
}

export async function getOutsidePremises(client: ApiClient): Promise<OutsidePremises> {
  return await client.get(`${BASE}/jobs/outside`, { schema: outsidePremisesSchema });
}

export async function getJobWork(client: ApiClient, jobWorkId: string): Promise<JobWorkOrder> {
  return await client.get(`${BASE}/jobs/${jobWorkId}`, { schema: jobWorkOrderSchema });
}

export async function createJobWork(
  client: ApiClient,
  input: CreateJobWorkInput,
): Promise<JobWorkOrder> {
  return await client.post(`${BASE}/jobs`, input, { schema: jobWorkOrderSchema });
}

export async function receiveJobWork(
  client: ApiClient,
  jobWorkId: string,
  input: ReceiveJobWorkInput,
): Promise<JobWorkOrder> {
  return await client.post(`${BASE}/jobs/${jobWorkId}/receive`, input, {
    schema: jobWorkOrderSchema,
  });
}

export async function getVendorPerformance(client: ApiClient): Promise<VendorPerformance> {
  return await client.get(`${BASE}/performance`, { schema: vendorPerformanceSchema });
}
